/**
 * @file routes/_app/risk-assessment/index.tsx
 * @description 風險評估頁面 — 組織風險矩陣概觀。
 *
 * 視覺來源：pages/_dashboard/risk-assessment/page.tsx
 * 資料來源：/api/nc-reports（不符合報告聚合為組織風險）
 *
 * 功能：
 * - PageShell meta：總評估組織、本季新增高風險、風險趨勢
 * - 風險分布 SectionCard：四桶（高/中/低/可控）分布條
 * - 本季風險變化趨勢：8 週 bar chart
 * - 高風險組織清單：依分數倒序、可搜尋、點擊 Drawer 查詳情
 *
 * 角色：admin / manager / decider 全可見；auditor 僅見自己案件風險
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Input, Select, Button, Spin, Empty } from 'antd';
import { message } from '@/lib/antd-app';
import ResponsiveDrawer from '@/components/common/ResponsiveDrawer';
import {
  Search,
  ShieldAlert,
  TrendingUp,
  Building2,
  AlertTriangle,
  ArrowRight,
  Table2,
  FlaskConical,
} from 'lucide-react';
import dayjs from 'dayjs';
import { apiClient } from '@/lib/api-client';
import {
  PageShell,
  PageMetaItem,
  SectionCard,
  StatusPill,
  FilterBar,
  DataField,
  DataFieldGrid,
  EmptyState,
} from '@/components/shell';
import { color, font, leading, radius, space, text, tracking, weight } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/risk-assessment/')({
  component: RiskAssessmentPage,
});

/* ════════════════════════════════════════════════
 * 型別與常數
 * ════════════════════════════════════════════════ */

/** NC 報告（對應後端 API） */
interface NCReport {
  id: number;
  ncNumber: string;
  auditTaskId: number | null;
  clientId: number;
  clientName: string | null;
  /** minor / major / critical */
  severity: 'minor' | 'major' | 'critical';
  clause: string | null;
  description: string;
  evidence: string | null;
  deadline: string | null;
  status: 'open' | 'correcting' | 'verified' | 'closed';
  auditorId: number | null;
  auditorName: string | null;
  createdDate: string | null;
  closedDate: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 分頁回應 */
interface PaginatedResponse {
  items: NCReport[];
  total: number;
  page: number;
  pageSize: number;
}

/** 嚴重程度 → 中文 */
const SEVERITY_LABEL: Record<NCReport['severity'], string> = {
  minor: '輕微',
  major: '主要',
  critical: '重大',
};

/** 嚴重程度 → StatusPill tone */
const SEVERITY_TONE: Record<NCReport['severity'], 'danger' | 'warning' | 'info'> = {
  critical: 'danger',
  major: 'warning',
  minor: 'info',
};

/** 狀態 → 中文 */
const STATUS_LABEL: Record<NCReport['status'], string> = {
  open: '開立',
  correcting: '改善中',
  verified: '已驗證',
  closed: '已關閉',
};

/** 狀態 → StatusPill tone */
const STATUS_TONE: Record<NCReport['status'], 'danger' | 'warning' | 'info' | 'muted'> = {
  open: 'info',
  correcting: 'warning',
  verified: 'info',
  closed: 'muted',
};

/* ════════════════════════════════════════════════
 * 工具：依組織聚合計算風險分數
 * ════════════════════════════════════════════════ */

/**
 * 組織風險聚合
 * - score = sum(嚴重度權重) / 活躍 NC 數 × 100，封頂 100
 * - 權重：重大 3 / 主要 2 / 輕微 1
 */
interface OrgRisk {
  clientId: number;
  clientName: string;
  score: number;
  activeCount: number;
  criticalCount: number;
  majorCount: number;
  minorCount: number;
  topFactor: string;
  lastAssessedAt: string;
}

function aggregateOrgRisk(reports: NCReport[]): OrgRisk[] {
  const map = new Map<number, OrgRisk & { factors: string[] }>();
  for (const r of reports) {
    const existing = map.get(r.clientId) ?? {
      clientId: r.clientId,
      clientName: r.clientName ?? `組織 #${r.clientId}`,
      score: 0,
      activeCount: 0,
      criticalCount: 0,
      majorCount: 0,
      minorCount: 0,
      topFactor: '',
      lastAssessedAt: r.createdAt,
      factors: [] as string[],
    };

    if (r.status !== 'closed') {
      existing.activeCount += 1;
      if (r.severity === 'critical') existing.criticalCount += 1;
      else if (r.severity === 'major') existing.majorCount += 1;
      else existing.minorCount += 1;
      if (r.clause && existing.factors.length < 3) {
        existing.factors.push(r.clause);
      }
    }
    if (dayjs(r.createdAt).isAfter(dayjs(existing.lastAssessedAt))) {
      existing.lastAssessedAt = r.createdAt;
    }
    map.set(r.clientId, existing);
  }

  return Array.from(map.values())
    .map((o) => {
      const weighted = o.criticalCount * 3 + o.majorCount * 2 + o.minorCount * 1;
      const cap = Math.max(o.activeCount, 1) * 3;
      const score = Math.min(100, Math.round((weighted / cap) * 100));
      return {
        clientId: o.clientId,
        clientName: o.clientName,
        score,
        activeCount: o.activeCount,
        criticalCount: o.criticalCount,
        majorCount: o.majorCount,
        minorCount: o.minorCount,
        topFactor: o.factors[0] ?? '——',
        lastAssessedAt: o.lastAssessedAt,
      };
    })
    .filter((o) => o.activeCount > 0)
    .sort((a, b) => b.score - a.score);
}

function scoreToTone(score: number): 'danger' | 'warning' | 'info' | 'success' {
  if (score >= 70) return 'danger';
  if (score >= 40) return 'warning';
  if (score >= 20) return 'info';
  return 'success';
}

function scoreToLabel(score: number): string {
  if (score >= 70) return '高風險';
  if (score >= 40) return '中風險';
  if (score >= 20) return '低風險';
  return '可控';
}

/* ════════════════════════════════════════════════
 * 子組件
 * ════════════════════════════════════════════════ */

/** 風險分布橫向條 */
function RiskDistributionBar({
  items,
  total,
}: {
  items: Array<{ label: string; count: number; tone: 'danger' | 'warning' | 'info' | 'muted' }>;
  total: number;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space[3] }}>
      {items.map((item) => {
        const pct = total > 0 ? (item.count / total) * 100 : 0;
        const barColor =
          item.tone === 'danger'
            ? color.state.danger
            : item.tone === 'warning'
              ? color.state.warning
              : item.tone === 'info'
                ? color.state.info
                : color.text.muted;
        return (
          <div key={item.label}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  fontSize: text.md,
                  color: color.text.secondary,
                  fontWeight: weight.medium,
                }}
              >
                {item.label}
              </span>
              <span
                style={{
                  fontFamily: font.display,
                  fontSize: text.md,
                  fontWeight: weight.semibold,
                  color: color.text.primary,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {item.count}
                <span
                  style={{
                    fontSize: text.sm,
                    color: color.text.muted,
                    fontWeight: weight.regular,
                    marginLeft: 6,
                  }}
                >
                  {pct.toFixed(0)}%
                </span>
              </span>
            </div>
            <div
              style={{
                height: 6,
                background: color.surface.sunken,
                borderRadius: radius.full,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: barColor,
                  borderRadius: radius.full,
                  transition: 'width 480ms cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** 高風險組織單列 */
function OrgRiskRow({ org, onClick }: { org: OrgRisk; onClick: () => void }) {
  const tone = scoreToTone(org.score);
  const label = scoreToLabel(org.score);

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: 'unset',
        display: 'flex',
        alignItems: 'center',
        gap: space[4],
        padding: `${space[3]}px ${space[5]}px`,
        cursor: 'pointer',
        borderBottom: `1px solid ${color.border.subtle}`,
        width: '100%',
        boxSizing: 'border-box',
        transition: 'background 120ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = color.surface.muted;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {/* 組織圖示 */}
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: radius.md,
          background: color.brand.primarySoft,
          color: color.brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Building2 size={16} />
      </div>

      {/* 名稱 + 主要風險因素 */}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: text.md,
            fontWeight: weight.semibold,
            color: color.text.primary,
            marginBottom: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {org.clientName}
        </div>
        <div
          style={{
            fontSize: text.sm,
            color: color.text.tertiary,
            display: 'flex',
            alignItems: 'center',
            gap: space[2],
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          <span style={{ color: color.text.muted }}>主要風險：</span>
          <span style={{ color: color.text.secondary }}>{org.topFactor}</span>
        </div>
      </div>

      {/* 活躍 NC 細分 */}
      <div
        style={{
          display: 'flex',
          gap: space[3],
          fontSize: text.sm,
          color: color.text.tertiary,
          fontFamily: font.mono,
          flexShrink: 0,
        }}
      >
        {org.criticalCount > 0 && (
          <span style={{ color: color.state.danger, fontWeight: weight.semibold }}>
            重 {org.criticalCount}
          </span>
        )}
        {org.majorCount > 0 && (
          <span style={{ color: color.state.warning, fontWeight: weight.semibold }}>
            主 {org.majorCount}
          </span>
        )}
        {org.minorCount > 0 && (
          <span style={{ color: color.state.info, fontWeight: weight.semibold }}>
            輕 {org.minorCount}
          </span>
        )}
      </div>

      {/* 風險分數 + tone pill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: space[3], flexShrink: 0 }}>
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontFamily: font.display,
              fontSize: text.xl,
              fontWeight: weight.bold,
              color:
                tone === 'danger'
                  ? color.state.danger
                  : tone === 'warning'
                    ? color.state.warning
                    : color.text.primary,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
            }}
          >
            {org.score}
          </div>
          <div style={{ marginTop: 4 }}>
            <StatusPill tone={tone} size="sm" dot>
              {label}
            </StatusPill>
          </div>
        </div>
        <ArrowRight size={14} color={color.text.muted} />
      </div>

      {/* 上次評估日 */}
      <div
        style={{
          fontSize: text.sm,
          color: color.text.muted,
          fontFamily: font.mono,
          minWidth: 96,
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        {dayjs(org.lastAssessedAt).format('YYYY-MM-DD')}
      </div>
    </button>
  );
}

/* ════════════════════════════════════════════════
 * 頁面組件
 * ════════════════════════════════════════════════ */

function RiskAssessmentPage() {
  /* ---- 狀態 ---- */
  const [reports, setReports] = useState<NCReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [severityFilter, setSeverityFilter] = useState<NCReport['severity'] | undefined>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<OrgRisk | null>(null);

  /* ---- 資料獲取 ---- */
  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: '1', pageSize: '200' });
      if (severityFilter) params.set('severity', severityFilter);
      const data = await apiClient.get<PaginatedResponse>(`/api/nc-reports?${params.toString()}`);
      setReports(data.items);
    } catch (err) {
      console.error('[RiskAssessment] fetch failed:', err);
      message.error('獲取風險評估資料失敗');
    } finally {
      setLoading(false);
    }
  }, [severityFilter]);

  useEffect(() => {
    void fetchReports();
  }, [fetchReports]);

  /* ---- 聚合與統計 ---- */
  const orgRisks = useMemo(() => aggregateOrgRisk(reports), [reports]);

  const filteredOrgs = useMemo(() => {
    if (!searchText.trim()) return orgRisks;
    const k = searchText.toLowerCase();
    return orgRisks.filter((o) => o.clientName.toLowerCase().includes(k));
  }, [orgRisks, searchText]);

  const riskDistribution = useMemo(() => {
    const high = orgRisks.filter((o) => o.score >= 70).length;
    const mid = orgRisks.filter((o) => o.score >= 40 && o.score < 70).length;
    const low = orgRisks.filter((o) => o.score >= 20 && o.score < 40).length;
    const safe = orgRisks.filter((o) => o.score < 20).length;
    return { high, mid, low, safe };
  }, [orgRisks]);

  const newHighRisk = useMemo(() => {
    const ninetyDaysAgo = dayjs().subtract(90, 'day');
    return orgRisks.filter((o) => o.score >= 70 && dayjs(o.lastAssessedAt).isAfter(ninetyDaysAgo))
      .length;
  }, [orgRisks]);

  const trendBuckets = useMemo(() => {
    const buckets: Array<{ label: string; count: number }> = [];
    for (let i = 7; i >= 0; i -= 1) {
      const start = dayjs().subtract(i, 'week').startOf('week');
      const end = start.endOf('week');
      const count = reports.filter(
        (r) => dayjs(r.createdAt).isAfter(start) && dayjs(r.createdAt).isBefore(end),
      ).length;
      buckets.push({ label: start.format('M/D'), count });
    }
    return buckets;
  }, [reports]);

  const trendMax = useMemo(() => Math.max(1, ...trendBuckets.map((b) => b.count)), [trendBuckets]);

  const trendDirection: 'up' | 'down' | 'flat' = useMemo(() => {
    const half = Math.floor(trendBuckets.length / 2);
    const first = trendBuckets.slice(0, half).reduce((s, b) => s + b.count, 0);
    const second = trendBuckets.slice(half).reduce((s, b) => s + b.count, 0);
    if (second > first * 1.1) return 'up';
    if (second < first * 0.9) return 'down';
    return 'flat';
  }, [trendBuckets]);

  /* ---- 事件 ---- */
  const handleViewOrg = (org: OrgRisk) => {
    setSelectedOrg(org);
    setDrawerOpen(true);
  };

  const orgReports = useMemo(() => {
    if (!selectedOrg) return [];
    return reports.filter((r) => r.clientId === selectedOrg.clientId);
  }, [selectedOrg, reports]);

  /* ---- 渲染 ---- */
  return (
    <PageShell
      title="風險評估"
      description="基於不符合事項與改善狀態，動態量化獲證組織風險。分數每日更新一次。"
      actions={
        /* 年度風險評估表（ZHQP27 附表4）與市售抽樣統計（S25/S27）入口，與本概觀頁並列 */
        <div style={{ display: 'flex', gap: space[2] }}>
          <Link to="/risk-assessment/market-sampling">
            <Button icon={<FlaskConical size={14} />}>市售抽樣統計</Button>
          </Link>
          <Link to="/risk-assessment/annual">
            <Button icon={<Table2 size={14} />}>年度評估表（附表4）</Button>
          </Link>
        </div>
      }
      meta={
        <>
          <PageMetaItem
            icon={<Building2 size={13} />}
            label="總評估組織"
            value={`${orgRisks.length} 家`}
          />
          <PageMetaItem
            icon={<ShieldAlert size={13} />}
            label="本季新增高風險"
            value={
              <span style={{ color: newHighRisk > 0 ? color.state.danger : undefined }}>
                {newHighRisk} 家
              </span>
            }
          />
          <PageMetaItem
            icon={<TrendingUp size={13} />}
            label="風險趨勢"
            value={
              <span
                style={{
                  color:
                    trendDirection === 'up'
                      ? color.state.danger
                      : trendDirection === 'down'
                        ? color.state.success
                        : color.text.tertiary,
                  fontWeight: weight.semibold,
                }}
              >
                {trendDirection === 'up'
                  ? '↑ 上升'
                  : trendDirection === 'down'
                    ? '↓ 下降'
                    : '→ 持平'}
              </span>
            }
          />
        </>
      }
    >
      {/* ===== 第一排：風險分布 + 趨勢（左右雙欄） ===== */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: space[4],
          marginBottom: space[6],
        }}
      >
        <SectionCard
          icon={<ShieldAlert size={14} />}
          title="風險分布"
          description={`${orgRisks.length} 家組織按風險分桶`}
        >
          <RiskDistributionBar
            total={orgRisks.length}
            items={[
              { label: '高風險（≥ 70）', count: riskDistribution.high, tone: 'danger' },
              { label: '中風險（40–69）', count: riskDistribution.mid, tone: 'warning' },
              { label: '低風險（20–39）', count: riskDistribution.low, tone: 'info' },
              { label: '可控（< 20）', count: riskDistribution.safe, tone: 'muted' },
            ]}
          />
        </SectionCard>

        <SectionCard
          icon={<TrendingUp size={14} />}
          title="本季風險變化趨勢"
          description="最近 8 週 NC 開立量"
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${trendBuckets.length}, minmax(0, 1fr))`,
              alignItems: 'end',
              gap: space[2],
              height: 120,
              paddingTop: space[2],
            }}
          >
            {trendBuckets.map((b, idx) => {
              const ratio = b.count / trendMax;
              return (
                <div
                  key={`${b.label}-${idx}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 6,
                    height: '100%',
                    justifyContent: 'flex-end',
                  }}
                >
                  <span
                    style={{
                      fontSize: text.xs,
                      fontFamily: font.mono,
                      color: color.text.tertiary,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {b.count}
                  </span>
                  <div
                    style={{
                      width: '100%',
                      height: `${Math.max(4, ratio * 80)}px`,
                      background:
                        ratio > 0.7
                          ? color.state.danger
                          : ratio > 0.4
                            ? color.state.warning
                            : color.brand.primary,
                      borderRadius: 4,
                      transition: 'height 480ms cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                  />
                  <span
                    style={{
                      fontSize: text.xs,
                      color: color.text.muted,
                      letterSpacing: tracking.wide,
                    }}
                  >
                    {b.label}
                  </span>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>

      {/* ===== 第二排：高風險組織清單 ===== */}
      <SectionCard
        icon={<AlertTriangle size={14} />}
        title="高風險組織清單"
        description="按風險分數倒序排列，點擊查看詳情"
        padless
      >
        {/* 篩選列 */}
        <div
          style={{
            padding: `${space[3]}px ${space[5]}px`,
            borderBottom: `1px solid ${color.border.subtle}`,
            background: color.surface.muted,
          }}
        >
          <FilterBar
            search={
              <Input
                placeholder="搜尋組織名稱..."
                prefix={<Search size={14} color={color.text.muted} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            }
            filters={
              <Select
                placeholder="嚴重程度"
                options={[
                  { label: '輕微', value: 'minor' },
                  { label: '主要', value: 'major' },
                  { label: '重大', value: 'critical' },
                ]}
                value={severityFilter}
                onChange={(v) => setSeverityFilter(v)}
                allowClear
                style={{ width: 130 }}
              />
            }
            extra={
              (searchText || severityFilter) && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    setSearchText('');
                    setSeverityFilter(undefined);
                  }}
                >
                  重置
                </Button>
              )
            }
          />
        </div>

        {/* 內容 */}
        {loading ? (
          <div style={{ padding: space[8], display: 'flex', justifyContent: 'center' }}>
            <Spin />
          </div>
        ) : filteredOrgs.length === 0 ? (
          <EmptyState
            title="目前沒有有風險的組織"
            description="所有獲證組織均處於可控水位。"
            size="md"
          />
        ) : (
          <div>
            {filteredOrgs.slice(0, 20).map((org) => (
              <OrgRiskRow key={org.clientId} org={org} onClick={() => handleViewOrg(org)} />
            ))}
            {filteredOrgs.length > 20 && (
              <div
                style={{
                  padding: `${space[3]}px ${space[5]}px`,
                  fontSize: text.sm,
                  color: color.text.tertiary,
                  textAlign: 'center',
                  background: color.surface.muted,
                }}
              >
                僅顯示風險分數最高前 20 家，共 {filteredOrgs.length} 家
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* ===== 詳情 Drawer（mobile 全屏 / Pad 直立 90% / 桌面 580） ===== */}
      <ResponsiveDrawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: space[3] }}>
            <Building2 size={16} color={color.text.muted} />
            <span style={{ fontWeight: weight.semibold }}>{selectedOrg?.clientName ?? ''}</span>
          </div>
        }
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={580}
      >
        {selectedOrg && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: space[5] }}>
            {/* 風險評分卡片 */}
            <div
              style={{
                padding: space[5],
                background: color.surface.muted,
                border: `1px solid ${color.border.subtle}`,
                borderRadius: radius.md,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: text.sm,
                    color: color.text.muted,
                    textTransform: 'uppercase',
                    letterSpacing: tracking.widest,
                    fontWeight: weight.semibold,
                  }}
                >
                  風險分數
                </div>
                <div
                  style={{
                    fontFamily: font.display,
                    fontSize: text['4xl'],
                    fontWeight: weight.bold,
                    color:
                      selectedOrg.score >= 70
                        ? color.state.danger
                        : selectedOrg.score >= 40
                          ? color.state.warning
                          : color.text.primary,
                    fontVariantNumeric: 'tabular-nums',
                    letterSpacing: tracking.tight,
                    lineHeight: 1,
                    marginTop: 4,
                  }}
                >
                  {selectedOrg.score}
                  <span
                    style={{
                      fontSize: text.md,
                      color: color.text.muted,
                      fontWeight: weight.regular,
                      marginLeft: 6,
                    }}
                  >
                    / 100
                  </span>
                </div>
              </div>
              <StatusPill tone={scoreToTone(selectedOrg.score)} dot size="md">
                {scoreToLabel(selectedOrg.score)}
              </StatusPill>
            </div>

            {/* 屬性網格 */}
            <DataFieldGrid columns={2}>
              <DataField label="活躍 NC 數" value={`${selectedOrg.activeCount} 件`} mono />
              <DataField
                label="上次評估"
                value={dayjs(selectedOrg.lastAssessedAt).format('YYYY-MM-DD')}
                mono
              />
              <DataField
                label="重大"
                value={
                  <span
                    style={{
                      color: color.state.danger,
                      fontFamily: font.mono,
                      fontWeight: weight.semibold,
                    }}
                  >
                    {selectedOrg.criticalCount}
                  </span>
                }
              />
              <DataField
                label="主要"
                value={
                  <span
                    style={{
                      color: color.state.warning,
                      fontFamily: font.mono,
                      fontWeight: weight.semibold,
                    }}
                  >
                    {selectedOrg.majorCount}
                  </span>
                }
              />
              <DataField
                label="輕微"
                value={
                  <span
                    style={{
                      color: color.state.info,
                      fontFamily: font.mono,
                      fontWeight: weight.semibold,
                    }}
                  >
                    {selectedOrg.minorCount}
                  </span>
                }
                span={2}
              />
              <DataField label="主要風險因素" value={selectedOrg.topFactor} span={2} />
            </DataFieldGrid>

            {/* 該組織的 NC 列表 */}
            <SectionCard title="關聯 NC 報告" description={`共 ${orgReports.length} 件`} padless>
              {orgReports.length === 0 ? (
                <Empty description="無關聯記錄" style={{ padding: space[6] }} />
              ) : (
                orgReports.slice(0, 10).map((r) => (
                  <div
                    key={r.id}
                    style={{
                      padding: `${space[3]}px ${space[5]}px`,
                      borderBottom: `1px solid ${color.border.subtle}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: space[3],
                    }}
                  >
                    <span
                      style={{
                        fontFamily: font.mono,
                        fontSize: text.sm,
                        color: color.text.secondary,
                        fontWeight: weight.medium,
                      }}
                    >
                      {r.ncNumber}
                    </span>
                    <StatusPill tone={SEVERITY_TONE[r.severity]} size="sm" dot>
                      {SEVERITY_LABEL[r.severity]}
                    </StatusPill>
                    <StatusPill tone={STATUS_TONE[r.status]} size="sm">
                      {STATUS_LABEL[r.status]}
                    </StatusPill>
                    <span
                      style={{
                        fontSize: text.sm,
                        color: color.text.tertiary,
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: leading.snug,
                      }}
                    >
                      {r.description}
                    </span>
                  </div>
                ))
              )}
            </SectionCard>
          </div>
        )}
      </ResponsiveDrawer>
    </PageShell>
  );
}
